import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

const RegionalBreakdown = ({ regionalMoods }) => {
  const [displayData, setDisplayData] = useState([]);
  const [expandedRegion, setExpandedRegion] = useState(null);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (regionalMoods?.length) {
      setDisplayData(regionalMoods);
      hasLoadedOnce.current = true;
    }
  }, [regionalMoods]);

  if (!displayData?.length) {
    if (!hasLoadedOnce.current) {
      return (
        <div className="regional-empty">
          <span className="text-sm" style={{ color: 'var(--apple-text-tertiary)' }}>
            Loading regional data...
          </span>
        </div>
      );
    }
  }

  const handleRegionClick = (region) => {
    setExpandedRegion(expandedRegion === region ? null : region);
  };

  return (
    <div className="regional-list" data-testid="regional-breakdown">
      {displayData.map((region) => {
        const isExpanded = expandedRegion === region.region;
        const intensity = region.intensity || 0.5;
        
        return (
          <div key={region.region}>
            <div 
              className={`regional-row clickable ${isExpanded ? 'expanded' : ''}`}
              onClick={() => handleRegionClick(region.region)}
              data-testid={`region-${region.region.replace(/\s+/g, '-')}`}
              style={{ cursor: 'pointer' }}
            >
              <div className="regional-left">
                <div 
                  className="regional-dot" 
                  style={{ backgroundColor: region.color }} 
                />
                <span className="regional-name">{region.region}</span>
              </div>
              <div className="regional-right">
                <span 
                  className="regional-adjective" 
                  style={{ color: region.color }}
                >
                  {region.adjective}
                </span>
                <div className="regional-bar-track">
                  <div 
                    className="regional-bar-fill"
                    style={{ 
                      width: `${intensity * 100}%`,
                      backgroundColor: region.color 
                    }}
                  />
                </div>
                <span className="regional-expand-icon">
                  {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                </span>
              </div>
            </div>
            <AnimatePresence>
              {isExpanded && (
                <motion.div 
                  className="regional-details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`region-details-${region.region.replace(/\s+/g, '-')}`}
                >
                  <div className="regional-detail-grid">
                    <div className="regional-detail-item">
                      <span className="regional-detail-label">Intensity</span>
                      <span className="regional-detail-value">{Math.round(intensity * 100)}%</span>
                    </div>
                    <div className="regional-detail-item">
                      <span className="regional-detail-label">Mood</span>
                      <span className="regional-detail-value" style={{ color: region.color }}>
                        {region.adjective}
                      </span>
                    </div>
                    {region.pantone && (
                      <div className="regional-detail-item">
                        <span className="regional-detail-label">PANTONE</span>
                        <span className="regional-detail-value">{region.pantone?.name || ''}</span>
                      </div>
                    )}
                    {region.keywords?.length > 0 && (
                      <div className="regional-detail-item full-width">
                        <span className="regional-detail-label">Key Topics</span>
                        <div className="regional-keywords">
                          {region.keywords.slice(0, 4).map((kw, i) => (
                            <span key={i} className="regional-keyword-tag">{kw}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export { RegionalBreakdown };
